// ============================================================================
// REQUEST TO TICKET MAPPER
// ============================================================================
// Maps backend RequestDetails to UI DASTicketDetails format

import type { RequestDetails, RequestApprover, RequestAttachment, RequestStatus, RequestWorkflowHistory } from '../types/requests.types';
import type { DASTicketDetails, ApprovalLevel, TicketAttachment, WorkflowAction, ActionStatus, TicketRole } from '../types/dasTickets.types';
import type { Status, ID } from '../types/common.types';

/**
 * Extracts level number from levelName (e.g., "Level 1" -> 1, "Level 2" -> 2)
 */
function extractLevelNumber(levelName: string | undefined): number | null {
  if (!levelName) return null;
  const match = levelName.match(/level\s*(\d+)/i);
  return match ? parseInt(match[1], 10) : null;
}

/**
 * Cleans up duplicated comments in approval text and removes prefixes like "Reason:" and "Approver Comments:"
 * Handles various legacy data formats where the same text was repeated:
 * 1. "X\n\nReason: Y\n\nApprover Comments: Y" -> "X\n\nY" (keeps prefix text, removes duplicate)
 * 2. "Reason: X\n\nApprover Comments: X" -> "X"
 * 3. "Reason: X\nX" -> "X"
 * 4. "Reason: X" -> "X"
 * 5. "X. X" or "X.X" or "X. X." (period separator) -> "X"
 * 6. "X\nX" (newline separator) -> "X"
 * 7. "X X" (space separator for short text) -> "X"
 * 8. General duplication detection with any separator
 */
function cleanupDuplicatedComments(text: string | undefined): string | undefined {
  if (!text) return text;

  let cleanedText = text.trim();

  console.log('[cleanupDuplicatedComments-mapper] Input:', JSON.stringify(cleanedText));

  // Pattern E (NEW - Most specific): Direct "Reason: X\nApprover Comments: X" with single newline
  // e.g., "Reason: Lack of document support\nApprover Comments: Lack of document support"
  const singleNewlineMatch = cleanedText.match(/^Reason:\s*([\s\S]+?)\s*\nApprover Comments:\s*([\s\S]+?)$/);
  if (singleNewlineMatch) {
    const reason = singleNewlineMatch[1].trim();
    const comments = singleNewlineMatch[2].trim();
    console.log('[cleanupDuplicatedComments-mapper] Pattern E matched - single newline:', { reason, comments });
    if (reason === comments) {
      console.log('[cleanupDuplicatedComments-mapper] Pattern E: Returning reason only');
      return reason;
    } else {
      return `${reason}\n\n${comments}`;
    }
  }

  // Pattern F (NEW): Handle prefix + single newline separators
  // e.g., "Rejected via consolidation CON-123\nReason: X\nApprover Comments: X"
  const prefixSingleNewlineMatch = cleanedText.match(/^([\s\S]+?)\nReason:\s*([\s\S]+?)\s*\nApprover Comments:\s*([\s\S]+?)$/);
  if (prefixSingleNewlineMatch) {
    const prefix = prefixSingleNewlineMatch[1].trim();
    const reason = prefixSingleNewlineMatch[2].trim();
    const comments = prefixSingleNewlineMatch[3].trim();
    console.log('[cleanupDuplicatedComments-mapper] Pattern F matched - prefix with single newlines:', { prefix, reason, comments });
    if (reason === comments) {
      cleanedText = `${prefix}\n\n${reason}`;
      console.log('[cleanupDuplicatedComments-mapper] Pattern F: Returning prefix + reason');
      return cleanedText;
    } else {
      return `${prefix}\n\n${reason}\n\n${comments}`;
    }
  }

  // Pattern A: Handle text with prefix content followed by "Reason: X\n\nApprover Comments: X"
  // e.g., "Rejected via consolidation CON-123\n\nReason: Lack of support\n\nApprover Comments: Lack of support"
  const prefixReasonCommentsMatch = cleanedText.match(/^([\s\S]+?)\n\nReason:\s*([\s\S]+?)\n\nApprover Comments:\s*([\s\S]+?)$/);
  if (prefixReasonCommentsMatch) {
    const prefix = prefixReasonCommentsMatch[1].trim();
    const reason = prefixReasonCommentsMatch[2].trim();
    const comments = prefixReasonCommentsMatch[3].trim();
    console.log('[cleanupDuplicatedComments-mapper] Pattern A matched:', { prefix, reason, comments });
    // If reason and comments are the same, keep prefix + reason only
    if (reason === comments) {
      cleanedText = `${prefix}\n\n${reason}`;
    } else {
      // Keep all parts but remove the "Reason:" and "Approver Comments:" labels
      cleanedText = `${prefix}\n\n${reason}\n\n${comments}`;
    }
    return cleanedText;
  }

  // Pattern B: Handle "Reason: X\n\nApprover Comments: Y" format (at start of text)
  const reasonAndCommentsMatch = cleanedText.match(/^Reason:\s*([\s\S]+?)\n\nApprover Comments:\s*([\s\S]+?)$/);
  if (reasonAndCommentsMatch) {
    const reason = reasonAndCommentsMatch[1].trim();
    const comments = reasonAndCommentsMatch[2].trim();
    console.log('[cleanupDuplicatedComments-mapper] Pattern B matched:', { reason, comments });
    // If reason and comments are the same, just return the reason
    if (reason === comments) {
      return reason;
    } else {
      // Return both without prefixes, separated by newline
      return `${reason}\n\n${comments}`;
    }
  }

  // Pattern G (NEW): Generic pattern - find and remove duplicate Approver Comments when same as Reason
  // This handles any format with "Reason:" and "Approver Comments:" labels
  if (cleanedText.includes('Reason:') && cleanedText.includes('Approver Comments:')) {
    // Extract content after each label, handling multiline content
    const reasonIdx = cleanedText.indexOf('Reason:');
    const commentsIdx = cleanedText.indexOf('Approver Comments:');

    if (reasonIdx !== -1 && commentsIdx !== -1 && reasonIdx < commentsIdx) {
      // Get text between "Reason:" and "Approver Comments:"
      const reasonStart = reasonIdx + 'Reason:'.length;
      const reasonContent = cleanedText.substring(reasonStart, commentsIdx).trim();

      // Get text after "Approver Comments:"
      const commentsStart = commentsIdx + 'Approver Comments:'.length;
      const commentsContent = cleanedText.substring(commentsStart).trim();

      console.log('[cleanupDuplicatedComments-mapper] Pattern G check:', { reasonContent, commentsContent });

      if (reasonContent === commentsContent) {
        // Get any prefix text before "Reason:"
        const prefixText = cleanedText.substring(0, reasonIdx).trim();

        if (prefixText) {
          console.log('[cleanupDuplicatedComments-mapper] Pattern G: Returning prefix + reason');
          return `${prefixText}\n\n${reasonContent}`;
        } else {
          console.log('[cleanupDuplicatedComments-mapper] Pattern G: Returning reason only');
          return reasonContent;
        }
      }
    }
  }

  // Pattern C: Handle "Reason: X" format (strip the prefix)
  if (cleanedText.startsWith('Reason:')) {
    cleanedText = cleanedText.substring(7).trim();
    console.log('[cleanupDuplicatedComments-mapper] Pattern C: Stripped Reason prefix');
  }

  // Pattern D: Handle text with Reason and Approver Comments anywhere (with other content before/between)
  // Look for "Reason: X" followed later by "Approver Comments: X" (same content)
  const reasonMatch = cleanedText.match(/Reason:\s*([^\n]+)/);
  const commentsMatch = cleanedText.match(/Approver Comments:\s*([^\n]+)/);
  if (reasonMatch && commentsMatch) {
    const reasonText = reasonMatch[1].trim();
    const commentsText = commentsMatch[1].trim();
    console.log('[cleanupDuplicatedComments-mapper] Pattern D check:', { reasonText, commentsText });
    if (reasonText === commentsText) {
      // Remove the "Approver Comments: X" line entirely
      cleanedText = cleanedText.replace(/\n*Approver Comments:\s*[^\n]+/, '');
      // Also remove the "Reason:" prefix
      cleanedText = cleanedText.replace(/Reason:\s*/, '');
      cleanedText = cleanedText.trim();
      console.log('[cleanupDuplicatedComments-mapper] Pattern D: Removed duplicates');
      return cleanedText;
    }
  }

  // Now handle simple duplication patterns on the cleaned text

  // Pattern 1: Period separator "X. X" or "X.X" or "X. X." (with optional trailing period)
  const periodParts = cleanedText.split(/[.\u3002\uff0e]\s*/).filter(part => part.trim() !== '');
  if (periodParts.length === 2) {
    const part1 = periodParts[0].trim();
    const part2 = periodParts[1].trim();
    if (part1 && part2 && part1 === part2) {
      return part1;
    }
  }
  if (periodParts.length > 2) {
    const firstPart = periodParts[0].trim();
    const allSame = periodParts.every(part => part.trim() === firstPart);
    if (allSame && firstPart) {
      return firstPart;
    }
  }

  // Pattern 2: Newline separator "X\nX" or multiple newlines
  const newlineParts = cleanedText.split(/\n+/).filter(part => part.trim() !== '');
  if (newlineParts.length === 2) {
    const part1 = newlineParts[0].trim();
    const part2 = newlineParts[1].trim();
    if (part1 && part2 && part1 === part2) {
      return part1;
    }
  }
  if (newlineParts.length > 2) {
    const firstPart = newlineParts[0].trim();
    const allSame = newlineParts.every(part => part.trim() === firstPart);
    if (allSame && firstPart) {
      return firstPart;
    }
  }

  // Pattern 3: Space separator for exactly duplicated text "X X"
  const halfLength = Math.floor(cleanedText.length / 2);
  if (cleanedText.length > 1 && cleanedText.length % 2 === 1) {
    const firstHalf = cleanedText.substring(0, halfLength).trim();
    const secondHalf = cleanedText.substring(halfLength + 1).trim();
    if (firstHalf && secondHalf && firstHalf === secondHalf && cleanedText[halfLength] === ' ') {
      return firstHalf;
    }
  }

  // Pattern 4: General duplication detection
  const generalSeparatorParts = cleanedText.split(/[\s]*[.,;:\-–—|/\\]+[\s]*/).filter(part => part.trim() !== '');
  if (generalSeparatorParts.length === 2) {
    const part1 = generalSeparatorParts[0].trim();
    const part2 = generalSeparatorParts[1].trim();
    if (part1 && part2 && part1 === part2) {
      return part1;
    }
  }

  return cleanedText;
}

/**
 * Determines the current user's role for this request
 */
function determineUserRole(request: RequestDetails, currentUserId?: ID): TicketRole {
  if (!currentUserId) return 'Viewer';

  // Check if user is the creator
  if (request.submittedBy?.toString() === currentUserId.toString()) {
    return 'Creator';
  }

  // Check if user is an approver
  const approvers = request.approvers || [];
  const userApprover = approvers.find(
    a => a.approverUserId?.toString() === currentUserId.toString() ||
         a.actualApproverUserId?.toString() === currentUserId.toString()
  );

  if (userApprover) {
    // Check if this is a recommender (not final approver)
    if (!userApprover.isFinalApprover) {
      return 'Recommender';
    }
    return 'Approver';
  }

  return 'Viewer';
}

/**
 * Determines the current user's action status for this request
 */
function determineUserActionStatus(request: RequestDetails, currentUserId?: ID, userRole?: TicketRole): ActionStatus {
  if (!currentUserId) return 'No Action Required';

  // For returned requests - check if user is the one the request was returned to
  if (request.currentStatus === 'Returned') {
    // If returnedToUserId is set, only that user needs to take action
    if (request.returnedToUserId) {
      if (request.returnedToUserId.toString() === currentUserId.toString()) {
        return 'Pending – Your action needed';
      }
      // For creators: show "No Action Required" if request was returned to someone else
      if (userRole === 'Creator') {
        return 'No Action Required';
      }
    } else {
      // Legacy behavior: if returnedToUserId is not set, creator takes action
      if (userRole === 'Creator') {
        return 'Pending – Your action needed';
      }
    }
  }

  // For creators on non-returned requests - no action needed
  if (userRole === 'Creator') {
    return 'No Action Required';
  }

  // For approvers/recommenders
  const approvers = request.approvers || [];
  const userApprover = approvers.find(
    a => a.approverUserId?.toString() === currentUserId.toString() ||
         a.actualApproverUserId?.toString() === currentUserId.toString()
  );

  if (userApprover) {
    const status = userApprover.approvalStatus?.toLowerCase();
    const action = userApprover.approvalAction?.toLowerCase();

    // Check if already actioned
    if (status === 'approved' || action?.includes('approved')) {
      return 'Approved by You';
    }
    if (status === 'rejected' || action?.includes('rejected')) {
      return 'Rejected by You';
    }
    if (action?.includes('recommended') && action?.includes('positive')) {
      return 'Recommended – Positive';
    }
    if (action?.includes('recommended') && action?.includes('adverse')) {
      return 'Recommended – Adverse';
    }
    if (status === 'returned' || action?.includes('returned')) {
      return 'Sent Back by You';
    }

    // Check if pending and it's their turn
    if (status === 'pending' && request.currentStatus !== 'Approved' && request.currentStatus !== 'Rejected' && request.currentStatus !== 'Cancelled') {
      // For sequential workflows, check if it's the user's turn based on LEVEL (not sequenceOrder)
      // This allows multiple approvers at the same level to act simultaneously
      const pendingApprovers = approvers.filter(a => a.approvalStatus?.toLowerCase() === 'pending');

      // Extract level from levelName for each pending approver
      const pendingWithLevels = pendingApprovers.map(a => ({
        ...a,
        extractedLevel: extractLevelNumber(a.levelName) ?? a.sequenceOrder,
      }));

      // Find the minimum pending level
      const minPendingLevel = Math.min(...pendingWithLevels.map(a => a.extractedLevel));

      // Get the user's level
      const userLevel = extractLevelNumber(userApprover.levelName) ?? userApprover.sequenceOrder;

      // User can act if they're at the minimum pending level
      if (userLevel === minPendingLevel) {
        return 'Pending – Your action needed';
      }

      // User IS an approver and the row is Pending, but it's NOT their turn
      // (an earlier level still has a Pending approver). Under
      // ShowWithRestriction this is the "preview" case — typically the
      // final approver seeing the request before predecessors have acted.
      // Mirror the dashboard's label so the detail-view status text stays
      // consistent. The Take Action button remains hidden because it gates
      // on the 'Pending – Your action needed' literal elsewhere.
      return 'Pending – Awaiting previous approver';
    }
  }

  return 'No Action Required';
}

/**
 * Maps backend RequestDetails to UI DASTicketDetails format
 * @param request - The request details from the backend
 * @param currentUserId - The current logged-in user's ID (optional, for role/status calculation)
 */
export const mapRequestDetailsToTicket = (request: RequestDetails, currentUserId?: ID): DASTicketDetails => {
  // Map approvers to approval hierarchy (flat structure)
  // Pass the request status and workflow history to handle consolidated approvals where final approver
  // data might not be directly updated on the child request's approver record
  const approvalHierarchy = mapApproversToHierarchy(
    request.approvers || [],
    request.currentStatus,
    request.workflowHistory || []
  );

  // Map attachments to ticket attachment format
  const attachments = mapAttachmentsToTicketFormat(request.attachments || []);

  // Determine current user's role and action status
  const yourRole = determineUserRole(request, currentUserId);
  const yourActionStatus = determineUserActionStatus(request, currentUserId, yourRole);

  return {
    // Base entity fields
    id: request.id,
    status: mapRequestStatusToEntityStatus(request.currentStatus),
    createdAt: request.createdAt,
    updatedAt: request.updatedAt,
    createdBy: request.createdBy,
    updatedBy: request.updatedBy,

    // Ticket fields (mapped from Request fields)
    ticketNo: request.requestNumber,
    externalReferenceNo: request.externalReferenceNo ?? null,
    subject: request.title,
    description: request.description || '',
    requestType: (request.requestTypeName as any) || 'General Request',
    requestTypeId: request.requestTypeId,
    priority: (request.priority as any) || 'Normal',

    // Map workflow data to ticket stages
    // For 'Returned' status, always use 'Returned' as the stage so UI shows creator actions
    // Otherwise use currentWorkflowStep if available, or fall back to status mapping
    yourRole,
    currentStage: request.currentStatus === 'Returned'
      ? 'Returned'
      : (request.currentWorkflowStep || mapStatusToStage(request.currentStatus)),
    yourActionStatus,

    // Dates
    pendingSince: request.submittedAt,
    pendingAge: calculateDaysSince(request.submittedAt),
    ticketAge: calculateDaysSince(request.createdAt),

    // Creator info
    createdByUserId: request.submittedBy.toString(),
    createdByUserName: request.submittedByName || 'Unknown',

    // Value and Required By Date
    value: request.requestValue,
    requiredByDate: request.requiredByDate,

    // Approval hierarchy (flat list of approvers)
    approvalHierarchy,

    // Related tickets - IDs for backward compatibility
    relatedTickets: request.relatedTicketIds?.map(id => id.toString()) || [],

    // Related ticket details - full info from backend
    relatedTicketDetails: request.relatedTickets?.map(ticket => ({
      id: ticket.id.toString(),
      requestNumber: ticket.requestNumber,
      requestTypeName: ticket.requestTypeName,
      subject: ticket.subject,
      createdByUserName: ticket.createdByUserName,
      createdAt: ticket.createdAt,
      currentStatus: ticket.currentStatus,
      relationshipType: ticket.relationshipType,
    })) || [],

    // Attachments - mapped from backend RequestAttachment to UI TicketAttachment format
    attachments,

    // Workflow history - mapped from backend or generated from request creation data
    workflowHistory: mapWorkflowHistory(request),

    // Document count
    documentCount: attachments.length,

    // Return tracking fields
    returnedToUserId: request.returnedToUserId?.toString(),
    returnedToUserName: request.returnedToUserName,

    // Row version
    rowVersion: request.rowVersion?.toString(),

    // Consolidation fields (for two-level approval workflow)
    isConsolidatedRequest: request.isConsolidatedRequest || false,
    consolidatedItemCount: request.consolidatedItemCount,
    consolidatedTotalValue: request.consolidatedTotalValue,
    // consolidatedItems will be loaded separately via API call
  };
};

/**
 * Maps backend attachments to UI ticket attachment format
 */
function mapAttachmentsToTicketFormat(attachments: RequestAttachment[]): TicketAttachment[] {
  return attachments.map(att => ({
    id: att.id,
    fileName: att.originalFileName || att.fileName,
    documentTitle: att.documentTitle || att.originalFileName || att.fileName,
    documentDescription: att.documentDescription,
    fileSize: att.fileSizeBytes || 0,
    fileType: att.mimeType || att.fileType || 'application/octet-stream',
    uploadedBy: att.uploadedByUserName || att.createdByName || 'Unknown',
    uploadedAt: att.uploadedAt || att.createdAt,
    // Use backend-provided downloadUrl if available, otherwise construct with correct path
    downloadUrl: att.downloadUrl || `/api/v1/das/attachments/${att.id}/download`,
  }));
}

/**
 * Finds the final approval/rejection action from workflow history
 * Used as fallback when approver record doesn't have date/comments
 */
function findFinalApprovalFromHistory(
  workflowHistory: RequestWorkflowHistory[],
  isRejected: boolean
): RequestWorkflowHistory | null {
  // Sort by date descending to find the most recent action first
  const sortedHistory = [...workflowHistory].sort(
    (a, b) => new Date(b.actionAt).getTime() - new Date(a.actionAt).getTime()
  );

  // Priority 1: Look for explicit final approval/rejection actions (case-insensitive)
  const finalAction = sortedHistory.find((action) => {
    const actionLower = action.actionType.toLowerCase();
    return actionLower === 'finalapproved' ||
           actionLower === 'final approved' ||
           actionLower === 'finalrejected' ||
           actionLower === 'final rejected' ||
           actionLower.includes('final approv') ||
           actionLower.includes('final reject');
  });

  if (finalAction) return finalAction;

  // Priority 2: For non-final actions, find the last approval/rejection that is NOT a pre-approval
  const targetAction = isRejected ? 'reject' : 'approv';
  return sortedHistory.find((action) => {
    const actionLower = action.actionType.toLowerCase();
    // Exclude pre-approvals and consolidation creation actions
    const isPreApproval = actionLower.includes('pre') && !actionLower.includes('reject');
    const isCreation = actionLower.includes('created') || actionLower.includes('submitted');
    return actionLower.includes(targetAction) && !isPreApproval && !isCreation;
  }) || null;
}

/**
 * Maps approvers to approval hierarchy levels (flat structure for UI)
 * Groups approvers by their levelName rather than sequenceOrder
 * @param approvers - List of approvers from the request
 * @param requestStatus - Overall request status (used to determine final approver status for consolidated requests)
 * @param workflowHistory - Workflow history for fallback data when approver record is incomplete
 */
function mapApproversToHierarchy(
  approvers: RequestApprover[],
  requestStatus?: string,
  workflowHistory: RequestWorkflowHistory[] = []
): ApprovalLevel[] {
  // Find the max sequence order to identify the final approver if not explicitly set
  const maxSequenceOrder = Math.max(...approvers.map(a => a.sequenceOrder), 0);

  // Check if any approver has isFinalApprover explicitly set to true
  const hasExplicitFinalApprover = approvers.some(a => a.isFinalApprover === true);

  // Determine if request is in a final completed state
  // Note: "Pre Approved" is NOT a completed state - the final approver still needs to act
  const requestStatusLower = (requestStatus || '').toLowerCase();
  const isPreApprovedStatus = requestStatusLower === 'pre approved' || requestStatusLower === 'preapproved';
  const isRequestRejected = requestStatusLower === 'rejected' || requestStatusLower.includes('rejected');
  const isRequestApproved = !isPreApprovedStatus && (requestStatusLower === 'approved' || requestStatusLower.includes('approved'));
  const isRequestCompleted = isRequestRejected || isRequestApproved;

  // Find final approval action from history for fallback
  const finalHistoryAction = isRequestCompleted && workflowHistory.length > 0
    ? findFinalApprovalFromHistory(workflowHistory, isRequestRejected)
    : null;

  // Extract level numbers from levelName for all approvers
  const approversWithExtractedLevels = approvers.map(approver => ({
    ...approver,
    extractedLevel: extractLevelNumber(approver.levelName),
  }));

  // Find the max level from non-final approvers to calculate final approver's display level
  const nonFinalApproversLevels = approversWithExtractedLevels
    .filter(a => !a.isFinalApprover)
    .map(a => a.extractedLevel)
    .filter((l): l is number => l !== null);
  const maxNonFinalLevel = nonFinalApproversLevels.length > 0
    ? Math.max(...nonFinalApproversLevels)
    : 0;

  const mapped = approversWithExtractedLevels.map(approver => {
    const isFinal = hasExplicitFinalApprover
      ? approver.isFinalApprover
      : approver.sequenceOrder === maxSequenceOrder;

    // Determine the display level:
    // 1. Use extracted level from levelName if available (e.g., "Level 1" -> 1)
    // 2. For final approvers without extracted level, use maxNonFinalLevel + 1
    // 3. Fall back to sequenceOrder as last resort
    let displayLevel: number;
    if (approver.extractedLevel !== null) {
      displayLevel = approver.extractedLevel;
    } else if (isFinal) {
      displayLevel = maxNonFinalLevel + 1;
    } else {
      displayLevel = approver.sequenceOrder;
    }

    // Determine the approval status
    // For final approvers: if the overall request is Approved/Rejected but the approver's
    // individual status wasn't updated (e.g., in consolidated approvals), infer from request status
    let approvalStatus = mapApprovalStatus(approver.approvalStatus, approver.approvalAction);

    // Get actionDate and comments from approver record
    let actionDate = approver.approvalAt;
    let comments = approver.approvalComments;

    // If this is the final approver and status is still Pending but request is completed,
    // override the status based on request outcome (handles legacy data before backend fix)
    // Note: Do NOT override if request is only "Pre Approved" - that's not a final state
    if (isFinal && approvalStatus === 'Pending' && requestStatus) {
      const isPreApproved = requestStatusLower === 'pre approved' || requestStatusLower.includes('pre approved');
      if (!isPreApproved && isRequestCompleted) {
        approvalStatus = isRequestRejected ? 'Rejected' : 'Approved';
      }
    }

    // For final approvers with missing actionDate or comments, try workflow history fallback
    if (isFinal && isRequestCompleted && finalHistoryAction) {
      if (!actionDate) {
        actionDate = finalHistoryAction.actionAt;
      }
      if (!comments) {
        comments = finalHistoryAction.comments;
      }
    }

    return {
      level: displayLevel,
      levelName: approver.levelName || `Level ${displayLevel}`,
      approverName: approver.approverUserName || approver.approverRoleName || 'Unknown',
      approverRole: approver.approverType === 'role'
        ? (approver.approverRoleName || 'Role')
        : (approver.levelName || 'Approver'),
      status: approvalStatus,
      actionDate,
      comments: cleanupDuplicatedComments(comments),
      isFinalApprover: isFinal,
    };
  });

  return mapped;
}

/**
 * Maps workflow history from backend or creates default "Created" entry
 */
function mapWorkflowHistory(request: RequestDetails): WorkflowAction[] {
  // If we have workflow history from backend, map it
  if (request.workflowHistory && request.workflowHistory.length > 0) {
    return request.workflowHistory.map(history => ({
      id: history.id,
      action: mapActionType(history.actionType),
      actionBy: history.actionByName || 'Unknown', // Display name for UI
      actionByName: history.actionByName,
      actionByRole: 'User', // Could be enhanced if backend provides role info
      actionDate: history.actionAt,
      comments: cleanupDuplicatedComments(history.comments),
      level: undefined, // Could be enhanced if backend provides level info
    }));
  }

  // If no workflow history, create a default "Created" entry from request data
  return [{
    id: request.id,
    action: 'Created',
    actionBy: request.submittedByName || 'Unknown', // Display name for UI
    actionByName: request.submittedByName,
    actionByRole: 'Creator',
    actionDate: request.createdAt,
    comments: `Request "${request.title}" was created`,
    level: undefined,
  }];
}

/**
 * Maps backend action type to UI action type
 * Note: Order matters! Check specific consolidation types BEFORE generic patterns
 */
function mapActionType(actionType: string): WorkflowAction['action'] {
  // Return the original action type to preserve consolidation-specific types
  // The TicketHistory component will handle display formatting
  return actionType;
}

/**
 * Maps backend approval status to UI status
 * Checks both approvalStatus and approvalAction fields
 */
function mapApprovalStatus(status: string, action?: string): 'Pending' | 'Approved' | 'Rejected' | 'Skipped' | 'Pre Approved' {
  const statusLower = (status || '').toLowerCase();
  const actionLower = (action || '').toLowerCase();

  // Check for Pre Approved first (before general "approved" check)
  if (statusLower.includes('pre approved') || statusLower.includes('pre-approved') ||
      actionLower.includes('pre approved') || actionLower.includes('pre-approved')) return 'Pre Approved';
  // Check both status and action for approved state
  if (statusLower.includes('approved') || actionLower.includes('approved')) return 'Approved';
  if (statusLower.includes('rejected') || actionLower.includes('rejected')) return 'Rejected';
  if (statusLower.includes('skipped') || actionLower.includes('skipped')) return 'Skipped';
  return 'Pending';
}

/**
 * Maps request status to ticket stage
 */
function mapStatusToStage(status: string): any {
  // Map backend request status to UI ticket stage
  // Returns the status as-is for display when currentWorkflowStep is not available

  switch (status.toLowerCase()) {
    case 'approved':
      return 'Approved';
    case 'rejected':
      return 'Rejected';
    case 'cancelled':
      return 'Cancelled';
    case 'draft':
      return 'Draft';
    case 'submitted':
      return 'Submitted';
    case 'in review':
      return 'In Review';
    case 'pre approved':
      return 'Pre Approved';
    case 'on hold':
      return 'On Hold';
    case 'returned':
      return 'Returned';
    default:
      // Return the original status if no mapping found
      return status || 'Pending';
  }
}

/**
 * Calculate days since a given date
 */
function calculateDaysSince(dateString: string): number {
  const date = new Date(dateString);
  const now = new Date();
  const diffTime = Math.abs(now.getTime() - date.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
}

/**
 * Maps RequestStatus to BaseEntity Status
 */
function mapRequestStatusToEntityStatus(requestStatus: RequestStatus): Status {
  switch (requestStatus) {
    case 'Draft':
      return 'Draft';
    case 'Submitted':
    case 'In Review':
    case 'Pre Approved':
    case 'Returned':
      return 'Active';
    case 'On Hold':
      return 'Inactive';
    case 'Approved':
    case 'Rejected':
    case 'Cancelled':
      return 'Archived';
    default:
      return 'Active';
  }
}

export default mapRequestDetailsToTicket;
